import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useHistory } from 'react-router';
import { MediaQuery } from 'core/config';
import { useRenderSphere } from 'view/hooks';
import { SPHERE_COLOURS, SPEER_DEFAULT_META_DESCRIPTION } from 'core/utilities';
import { CanonicalTag } from 'view/components/common';
import { Hero, Reel, WhatWeDo, FeaturedProjects, Awards, CalendarCTA } from './sections';
import { Services } from './sections/Services';
import { PatentSection } from './sections/Patent';

const HOME_SEO_TITLE = 'Speer Technologies | Toronto Custom AI & Patentable Software';
const HOME_SEO_DESCRIPTION = SPEER_DEFAULT_META_DESCRIPTION;

export enum HomePageSections
{
    HERO    = 0,
    REEL = 1,
    WHATWEDO  = 2,
    SERVICES = 3,
    CONTACT = 4
}

type HomePageProps = {
    /** Audit landings: trimmed sections + audit hero. Omit for main homepage. */
    auditLanding?: 'audit' | 'audit-a' | 'footprint' | 'emr' | 'estate' | 'estate-1' | 'exposure-report' | 'fixme';
};

export const HomePage: React.FunctionComponent<HomePageProps> = ({ auditLanding }) =>
{
    const history = useHistory();
    const [ scrollPosition, setScrollPosition ] = useState(0);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const firstDivRef = useRef<HTMLDivElement>(null);
    const secondDivRef = useRef<HTMLDivElement>(null);
    const thirdDivRef = useRef<HTMLDivElement>(null);

    useRenderSphere(canvasRef, true, SPHERE_COLOURS.HOME_PAGE);

    const navigateToOurWork = () =>
    {
        history.push('/work');
    };

    const handleScroll = () =>
    {
        setScrollPosition(window.scrollY);
    };

    useEffect(() =>
    {
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const firstDiv = firstDivRef.current as HTMLDivElement;
    const secondDiv = secondDivRef.current as HTMLDivElement;
    const canvas = canvasRef.current as HTMLCanvasElement;
    const totalHeight = firstDiv?.clientHeight + secondDiv?.clientHeight  || 1;
    const percentageScrolled = scrollPosition / totalHeight;
    const scrollX = Math.min(percentageScrolled * 1.2, 1.2) * (firstDiv?.clientWidth - canvas?.clientWidth) * -1;
    const scrollY = percentageScrolled > 1 ? (scrollPosition - totalHeight) * -1 : 0;
    const translate = { x: scrollX , y: scrollY };

    return (
        <Container>
            {!auditLanding ? (
                <Helmet>
                    <title>{HOME_SEO_TITLE}</title>
                    <meta name="description" content={HOME_SEO_DESCRIPTION} />
                    <meta property="og:title" content={HOME_SEO_TITLE} />
                    <meta property="og:description" content={HOME_SEO_DESCRIPTION} />
                    <meta name="twitter:title" content={HOME_SEO_TITLE} />
                    <meta name="twitter:description" content={HOME_SEO_DESCRIPTION} />
                </Helmet>
            ) : null}
            <CanonicalTag
                url={
                    auditLanding === 'audit-a'
                        ? 'https://www.speer.io/audit-a'
                        : auditLanding === 'footprint'
                            ? 'https://www.speer.io/footprint'
                            : auditLanding === 'emr'
                                ? 'https://www.speer.io/emr'
                                : auditLanding === 'estate'
                                    ? 'https://www.speer.io/estate'
                                    : auditLanding === 'estate-1'
                                        ? 'https://www.speer.io/estate-1'
                                        : auditLanding === 'exposure-report'
                                            ? 'https://www.speer.io/exposure-report'
                                            : auditLanding === 'fixme'
                                                ? 'https://www.speer.io/fixme'
                                                : auditLanding === 'audit'
                                                    ? 'https://www.speer.io/audit'
                                                    : 'https://www.speer.io/'
                }
            />
            <div ref={firstDivRef}>
                <Hero
                    navigateToSection={navigateToOurWork}
                    variant={
                        auditLanding === 'audit-a'
                            ? 'audit-a'
                            : auditLanding === 'emr'
                                ? 'emr'
                                : auditLanding === 'estate'
                                    ? 'estate'
                                    : auditLanding === 'estate-1'
                                        ? 'estate-1'
                                        : auditLanding === 'exposure-report'
                                            ? 'exposure-report'
                                            : auditLanding === 'fixme'
                                                ? 'fixme'
                                                : auditLanding === 'audit' || auditLanding === 'footprint'
                                                    ? 'audit'
                                                    : 'default'
                    }
                />
            </div>
            <div ref={secondDivRef}>
                <Reel />
            </div>
            {!auditLanding ? (
                <>
                    <div ref={thirdDivRef}>
                        <WhatWeDo />
                    </div>
                    <Services />
                    <FeaturedProjects navigateToWork={navigateToOurWork} />
                    <PatentSection />
                    <Awards />
                    <CalendarCTA />
                </>
            ) : null}

            <CanvasWrapper translateX={translate.x} translateY={translate.y}>
                <Canvas
                    ref={canvasRef}
                    id="sphere"
                    className={'fade-in-sphere'}
                />
            </CanvasWrapper>
        </Container>
    );
};

const Container = styled.div`
    overflow: visible;
`;

const CanvasWrapper = styled.div<{ translateX: number, translateY: number }>`
    overflow: hidden;
    z-index: -1;

    @media ${MediaQuery.mobileS} {
        position: absolute;
        top: 1.7%;
        right: 0;
        width: 100vw;
        aspect-ratio: 1;
        height: auto;
    }
    @media ${MediaQuery.tablet} {
        top: -1.1%;
        right: -20%;
    }

    @media ${MediaQuery.laptopS} {
        position: fixed;
        top: 0;
        right: 0;
        width: auto;
        height: 100%;
        transform: ${({ translateX, translateY }) => `translate(${translateX}px, ${translateY}px)`};
        transition: top 0.1s linear;
    }
`;

const Canvas = styled.canvas`
    overflow: hidden;
    display: block;
    position: absolute;
    top: 45px;
    right: 0;
    width: 100%;
    height: 100%;
    transform: translate(25%, -25%);
    opacity: 0;
    transition: opacity 0.7s ease-in;

    &.fade-in-sphere {
        opacity: 1;
    }

    @media ${MediaQuery.tablet} {
        width: 100%;
        height: 100%;
        transform: none;
    }
`;
